import { isOpen } from './rideScreen';
import type { DriverLocation, Ride, RideStatus } from './types';

/**
 * The live ride as one pure reducer, fed by both the socket (`ride:updated`, `ride:driver-location`) and REST
 * (`GET /v1/rider/rides/current`, polled while the socket is down and while searching). Events can arrive out of
 * order — a poll that left before a push can land after it — so an update never moves a ride backwards.
 */
export type LiveState = { ride: Ride | null; source: 'socket' | 'rest' | 'local' | null };
export type LiveEvent =
  /** REST result: the open ride (or a just-completed ride the rider hasn't closed), or null */
  | { type: 'fetched'; ride: Ride | null }
  /** socket `ride:updated` (rider view) */
  | { type: 'pushed'; ride: Ride }
  /** socket `ride:driver-location` */
  | { type: 'location'; rideId: string; at: DriverLocation }
  /** the app itself changed the ride (booked, cancelled, tipped) or let it go (Done) */
  | { type: 'local'; ride: Ride | null };

export const initialLive: LiveState = { ride: null, source: null };

/** How far along a status is. Ended statuses outrank every open one; COMPLETED and the cancels share a rank. */
const RANK: Record<RideStatus, number> = {
  REQUESTED: 0,
  OFFERING: 1,
  ACCEPTED: 2,
  ARRIVED: 3,
  STARTED: 4,
  COMPLETED: 5,
  CANCELLED_BY_RIDER: 5,
  CANCELLED_BY_DRIVER: 5,
  CANCELLED_BY_ADMIN: 5,
  EXPIRED: 5,
};
export const rank = (s: RideStatus) => RANK[s] ?? 0;

/** OFFERING can follow REQUESTED and also show again (a re-offer); everything else only moves forward. */
function isOlder(next: Ride, cur: Ride): boolean {
  if (rank(next.status) < rank(cur.status)) return true;
  // same status: a payment that has moved on (PENDING → PAID) never goes back
  if (next.status === 'COMPLETED' && cur.status === 'COMPLETED') {
    const order = { PENDING: 0, FAILED: 0, PAID: 1, REFUNDED: 2 } as const;
    const a = cur.paymentStatus ? order[cur.paymentStatus] : -1;
    const b = next.paymentStatus ? order[next.paymentStatus] : -1;
    if (b < a) return true;
  }
  return false;
}

function merge(cur: Ride | null, next: Ride): Ride {
  if (!cur || cur.id !== next.id) return next;
  return {
    ...next,
    // matching progress isn't in every push; keep the last one while still searching
    matching: next.matching ?? (rank(next.status) <= 1 ? cur.matching : null),
    // socket pushes don't carry the road line: keep the one from the last HTTP read
    routePolyline: 'routePolyline' in next && next.routePolyline !== undefined ? next.routePolyline : cur.routePolyline,
    // a push or poll without a fresh driver position keeps the last one we had
    driver: next.driver ? { ...next.driver, location: next.driver.location ?? (cur.driver?.id === next.driver.id ? cur.driver.location : null) } : null,
  };
}

export function liveReducer(state: LiveState, ev: LiveEvent): LiveState {
  const cur = state.ride;
  switch (ev.type) {
    case 'local':
      return { ride: ev.ride, source: 'local' };
    case 'fetched': {
      if (!ev.ride) {
        // the server says nothing is open: keep an ended ride on screen (so its message can be read), drop the rest
        if (cur && isOpen(cur.status)) return { ride: null, source: 'rest' };
        return state.ride ? state : { ride: null, source: 'rest' };
      }
      if (cur && cur.id === ev.ride.id && isOlder(ev.ride, cur)) return state;
      return { ride: merge(cur, ev.ride), source: 'rest' };
    }
    case 'pushed': {
      const r = ev.ride;
      // a push about another, older ride never replaces the open one
      if (cur && cur.id !== r.id && isOpen(cur.status) && !isOpen(r.status)) return state;
      if (cur && cur.id === r.id && isOlder(r, cur)) return state;
      return { ride: merge(cur, r), source: 'socket' };
    }
    case 'location': {
      if (!cur || cur.id !== ev.rideId || !cur.driver || !isOpen(cur.status)) return state;
      const prev = cur.driver.location;
      if (prev && Date.parse(prev.at) > Date.parse(ev.at.at)) return state;
      return { ride: { ...cur, driver: { ...cur.driver, location: ev.at } }, source: 'socket' };
    }
  }
}

/**
 * How often to poll REST: every 3 s while searching (matching progress isn't pushed), every 5 s while the
 * socket is down, otherwise not at all (the socket pushes every change). `null` = don't poll.
 */
export function pollEvery(connected: boolean, ride: Ride | null): number | null {
  const searching = !!ride && rank(ride.status) <= 1 && isOpen(ride.status);
  if (searching) return 3000;
  if (!connected) return 5000;
  return null;
}
